import { apiClient } from '@/api/client'
import {
  companyPenaltiesPath,
  companyPenaltyTypesPath,
  companyPenaltyEntryApprovePath,
  companyPenaltyEntryReversePath,
  PENALTIES,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type {
  PenaltyType,
  PenaltyEntry,
  CreatePenaltyTypeDto,
  CreatePenaltyEntryDto,
  MyPenaltyListQuery,
  MyPenaltyListResponse,
} from '@/features/penalties/types/penalty.types'

export async function listPenaltyTypes(companyId: string): Promise<PenaltyType[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<PenaltyType[]>>(
    companyPenaltyTypesPath(companyId),
  )
  return Array.isArray(data?.data) ? data.data : []
}

export async function createPenaltyType(
  companyId: string,
  body: CreatePenaltyTypeDto,
): Promise<PenaltyType> {
  const { data } = await apiClient.post<ApiResponseWrapper<PenaltyType>>(
    companyPenaltyTypesPath(companyId),
    body,
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid create penalty type response')
  return doc
}

export async function createPenaltyEntry(
  companyId: string,
  body: CreatePenaltyEntryDto,
): Promise<PenaltyEntry> {
  const { data } = await apiClient.post<ApiResponseWrapper<PenaltyEntry>>(
    companyPenaltiesPath(companyId),
    body,
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid create penalty entry response')
  return doc
}

export async function approvePenaltyEntry(
  companyId: string,
  entryId: string,
): Promise<PenaltyEntry> {
  const { data } = await apiClient.post<ApiResponseWrapper<PenaltyEntry>>(
    companyPenaltyEntryApprovePath(companyId, entryId),
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid approve penalty entry response')
  return doc
}

/** Operator workspace: cross-company penalty entries. */
export async function getPenaltiesWorkspace(
  query?: MyPenaltyListQuery,
): Promise<MyPenaltyListResponse> {
  const { companyIds, ...rest } = query ?? {}
  const params: Record<string, unknown> = { ...rest }
  if (companyIds && companyIds.length > 0) {
    params.companyIds = companyIds.join(',')
  }
  const { data } = await apiClient.get<ApiResponseWrapper<MyPenaltyListResponse>>(
    PENALTIES,
    { params },
  )
  const payload = data?.data
  if (payload && Array.isArray(payload.data)) return payload
  return { data: [], pagination: { total: 0, page: 1, limit: query?.limit ?? 10 } }
}

export async function reversePenaltyEntry(
  companyId: string,
  entryId: string,
): Promise<PenaltyEntry> {
  const { data } = await apiClient.post<ApiResponseWrapper<PenaltyEntry>>(
    companyPenaltyEntryReversePath(companyId, entryId),
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid reverse penalty entry response')
  return doc
}
